import { ReactNode } from 'react';
import { SxProps, Theme } from '@mui/material';
import { Box } from '../../Layout/Box';
import { Typography } from '../../Typography';

export interface EntitySummaryProps {
  /**
   * The child elements to render in the summary grid.
   * Typically EntitySummaryField components, but can be any ReactNode.
   */
  children?: ReactNode;
  /**
   * Optional title for the summary section
   * @default "SUMMARY"
   */
  title?: string;
  /**
   * Whether to show the box shadow wrapper
   * @default true
   */
  showBoxShadow?: boolean;
  /**
   * Optional subtitle displayed inline after the title
   * Rendered in body1 variant
   */
  subtitle?: string;
  /**
   * Optional custom styles for the container box
   */
  containerSx?: SxProps<Theme>;
}

/**
 * EntitySummary - A reusable container component for displaying entity summary information
 *
 * This component provides a responsive grid layout for displaying field/value pairs
 * in a summary format. It uses a 2-column grid on tablet+ screens and a single column
 * on mobile devices.
 *
 * @example
 * ```tsx
 * <EntitySummary title="Asset Summary">
 *   <EntitySummaryField label="Species" value="Red Oak" />
 *   <EntitySummaryField label="DBH" value="25" unit="CM" />
 *   <EntitySummaryField label="Height" value="15" unit="M" />
 * </EntitySummary>
 * ```
 *
 * @example
 * ```tsx
 * // With custom styling
 * <EntitySummary
 *   title="Work Item Summary"
 *   showBoxShadow={false}
 *   containerSx={{ backgroundColor: 'grey.100' }}
 * >
 *   <EntitySummaryField label="Status" value="In Progress" />
 *   <EntitySummaryField label="Assignee" value="John Doe" />
 * </EntitySummary>
 * ```
 */
export const EntitySummary = ({
  children,
  title = 'SUMMARY',
  subtitle,
  showBoxShadow = true,
  containerSx,
}: EntitySummaryProps) => {
  // Container box styles - compose as array to support all SxProps shapes (objects, arrays, functions)
  const baseStyles = {
    m: 1,
    mb: 2,
    px: 1,
    bgcolor: 'background.paper',
    ...(showBoxShadow && {
      boxShadow: 1,
    }),
  };

  // Compose styles as array when containerSx is provided to preserve all SxProps types
  const boxStyles: SxProps<Theme> = containerSx
    ? ([baseStyles, containerSx] as SxProps<Theme>)
    : baseStyles;

  const fieldGridStyles: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: {
      xs: '1fr',
      sm: 'repeat(2, 1fr)',
    },
    rowGap: '0px',
    columnGap: '0px',
    width: '100%',
  };

  return (
    <Box sx={boxStyles}>
      {(title || subtitle) && (
        <Box sx={{ mb: 1 }}>
          {title && (
            <Typography variant="h1" component="span">
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography variant="body1" component="span" sx={{ ml: title ? 1 : 0 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      )}
      <Box sx={fieldGridStyles}>{children}</Box>
    </Box>
  );
};

EntitySummary.displayName = 'EntitySummary';

// Re-export EntitySummaryField for convenience
export { EntitySummaryField } from './EntitySummaryField';
export type { EntitySummaryFieldProps } from './EntitySummaryField';
